import { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  featureEnabled,
  useCategories,
  useLogout,
  useLowStockCount,
  useMe,
  usePendingPayments,
  useProducts,
  useSuppliers,
  useTenantSettings,
  useWarehouses,
} from '../lib/queries';
import { API_BASE, ApiError, onAuthFailure } from '../lib/api';
import { Icon } from './Icon';
import { setTheme as applyTheme, followParentTheme, hasManualOverride } from '../lib/theme';
import {
  buildReembedUrl,
  captureEmbedHandshake,
  getReferrerAccountId,
  isEmbedded,
  listenAccountChange,
} from '../lib/embed';
import { useTenantBrand } from '../lib/useTenantBrand';
import { usePricingMode } from '../lib/usePricingMode';
import { PricingModeOnboarding } from './PricingModeOnboarding';
import { SupportModeBanner } from './SupportModeBanner';

/**
 * Pantalla de error genérica para fallos NO-401 del API (5xx/network).
 * En modo embed no redirigimos a /login (la sesión la gestiona Whatsfy);
 * en standalone ofrecemos retry inline.
 */
function ErrorState({
  error,
  embedded,
  onRetry,
}: {
  error: unknown;
  embedded: boolean;
  onRetry: () => void;
}) {
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
      ? error
      : 'Error desconocido';
  return (
    <main className="splash">
      <div
        className="card"
        style={{
          padding: 20,
          maxWidth: 420,
          textAlign: 'center',
        }}
      >
        <h3 style={{ marginTop: 0 }}>Error de conexión</h3>
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {embedded
            ? 'No pudimos contactar el módulo. Recarga el módulo desde Whatsfy.'
            : 'No pudimos contactar el servidor. Verifica tu conexión y reintenta.'}
        </p>
        <p
          className="mono"
          style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 12 }}
        >
          {msg}
        </p>
        {!embedded && (
          <div style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-primary" onClick={onRetry}>
              Reintentar
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

interface ModuleTab {
  to: string;
  label: string;
  icon: Parameters<typeof Icon>[0]['name'];
  count?: number;
  countTone?: 'danger';
  // Sub-badge rojo opcional al lado del count principal (ej: "127 · 3 bajos").
  // Solo se renderiza si countDanger > 0.
  countDanger?: number;
  countDangerTitle?: string;
}

export function Layout() {
  const { data: me, isLoading, error, refetch } = useMe();
  const products = useProducts({ active: true, limit: 1 });
  const warehouses = useWarehouses(true);
  const suppliers = useSuppliers();
  const categories = useCategories();
  // Badge "stock bajo" del tab Productos. Endpoint dedicado que devuelve
  // {count} computado en SQL — antes lo calculábamos cliente-side desde
  // useInventoryLevels() pero el default limit=100 truncaba el conteo en
  // tenants con muchos levels (variants × warehouses). Bug invisible —
  // mostraba siempre <= ~5 incluso con cientos de SKUs bajos.
  const lowStockSummary = useLowStockCount();
  // Badge del tab "Pagos": count de pagos pendientes de verificar. limit:1
  // hace que el backend solo nos devuelva total + 1 row de relleno.
  const pendingPayments = usePendingPayments({
    status: 'pending_verification',
    limit: 1,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  // Brand whitelabel ADR-014 (F4-D): post-auth UI usa el brand del tenant
  // resuelto. F4-E implementa el lookup real; mientras tanto retorna null
  // y caemos al default 'Whatsfy'.
  const brand = useTenantBrand();
  const brandName = brand?.name ?? 'Whatsfy';

  // PRICE-C: trigger del onboarding modal. Lo mostramos cuando el comerciante
  // todavía NO eligió explícitamente su modo de precios. El backend siempre
  // devuelve `pricingModeExplicit` (boolean) en el config resuelto. Si es
  // false, mostramos el modal antes de que el comerciante interactúe con la
  // UI — la decisión es load-bearing porque cambia qué columnas se ven en
  // /products, qué tabs aparecen en el sidebar, etc.
  //
  // Se cierra automático al éxito de la mutación porque el query invalida
  // y `pricingModeExplicit` pasa a true. Si el user clickea "X" del modal,
  // mantenemos el flag dismissed local para no spamear (lo verá la próxima
  // sesión cuando reload).
  const tenantSettingsQuery = useTenantSettings();
  // PRICE-B: pricingMode debe leerse al top-level del componente (antes de
  // cualquier early return) para no violar la regla de hooks. React #310
  // si está después de los `return <Navigate>` que dependen de me/error.
  const pricingMode = usePricingMode();
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const pricingModeExplicit =
    tenantSettingsQuery.data?.config.pricingModeExplicit;
  // role del session viene del fetch /auth/me — puede ser undefined durante el
  // primer render. Mostramos el modal SOLO para owner/admin porque la
  // mutation backend está role-gated; un member vería el modal pero su click
  // resultaría en 403 con toast genérico — peor UX que no mostrarlo.
  const sessionRole = me?.session.role;
  const canChoosePricingMode =
    sessionRole === 'owner' || sessionRole === 'admin';
  const showOnboarding =
    !onboardingDismissed &&
    canChoosePricingMode &&
    pricingModeExplicit === false;

  // Menú de usuario (avatar dropdown) — abre/cierra con click en el avatar.
  const [menuOpen, setMenuOpen] = useState(false);
  const avatarBtnRef = useRef<HTMLButtonElement>(null);

  // Escape cierra el dropdown y devuelve foco al avatar (a11y).
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        // Microtask para que el cierre del menú no se trague el focus().
        queueMicrotask(() => avatarBtnRef.current?.focus());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // En modo iframe Whatsfy, la sesión se autocrea desde el handshake del
  // embed (account_id + admin_email). Cerrar sesión es contraproducente:
  // el user vuelve a abrir el módulo y se relogea automático. Ocultamos
  // el botón. En standalone (/login con magic link), sí lo mostramos.
  const embedded = isEmbedded();

  // F7-B: modal de sesión expirada — se dispara desde lib/api.ts cuando un
  // request recibe 401 Y el refresh también falla. JWT access es 15min y
  // refresh 30d con rotación; la mayoría del tiempo el refresh recupera silent.
  // Cuando NO recupera (refresh expiró, user revocado, family revoked),
  // mostramos modal con auto-reload en 5s.
  const [sessionExpired, setSessionExpired] = useState(false);

  // D1-F2b: alto real del banner de modo soporte (fixed). El componente lo
  // mide vía ResizeObserver y lo reporta — usamos ese valor como padding-top
  // del shell para que el banner no tape el contenido. 0 cuando no hay banner.
  const [supportBannerHeight, setSupportBannerHeight] = useState(0);

  // Suscripción al listener de fallos de auth. Una sola vez al montar.
  // El listener se reset en api.ts cuando un response vuelve a ser 2xx (user
  // reconectó), pero acá NO bajamos el flag local — el modal solo se cierra
  // por el botón explícito o por el reload automático.
  useEffect(() => {
    return onAuthFailure(() => setSessionExpired(true));
  }, []);

  // Reload automático tras 5s — el user puede recargar con el botón.
  const reconnect = () => {
    if (embedded) {
      const reembedUrl = buildReembedUrl(API_BASE);
      if (reembedUrl) {
        window.location.replace(reembedUrl);
        return;
      }
    }
    window.location.reload();
  };
  useEffect(() => {
    if (!sessionExpired) return;
    const t = window.setTimeout(() => reconnect(), 5000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionExpired]);

  // Capturar el handshake del embed cada vez que useMe() devuelve OK. Si
  // después la cookie expira y /auth/refresh falla, podemos re-embed con
  // estos params en lugar de caer al /login standalone (que dentro del
  // iframe es un dead-end). Solo aplica embedded — en standalone no
  // necesitamos el cache.
  useEffect(() => {
    if (!embedded || !me) return;
    captureEmbedHandshake({
      accountId: me.tenant.whatsfyAccountId,
      userEmail: me.session.email ?? '',
      accountName: me.tenant.name,
      locale: me.tenant.locale,
      tz: me.tenant.timezone,
    });
  }, [embedded, me]);

  // IFRAME-FIX-V2: capa 1 del fix de iframe persistente. Cuando el comerciante
  // cambia de cuenta en el panel host Whatsfy, el iframe NO se recarga
  // automáticamente y queda mostrando data del tenant viejo. Cross-origin
  // policy bloquea detección unilateral desde el iframe; necesitamos opt-in
  // del parent vía postMessage. El snippet del parent vive en
  // docs/whatsfy-injection-snippets.md.
  //
  // Cuando recibimos un mismatch entre el accountId nuevo del parent y el
  // tenant actual del módulo, hacemos hard redirect a /embed para que IFRAME-A
  // backend invalide la cookie vieja y reemita una nueva con el tenant correcto.
  //
  // Hook al top del componente (antes de early returns) por regla de hooks —
  // React #310 si va después de los `return <Navigate>`. El listener internamente
  // no-op cuando !embedded || !accountChangeWatchAccId.
  const accountChangeWatchAccId = me?.tenant?.whatsfyAccountId;
  useEffect(() => {
    if (!embedded || !accountChangeWatchAccId) return;
    return listenAccountChange((newAcc) => {
      if (!newAcc || newAcc === accountChangeWatchAccId) return;
      window.location.replace(
        `${API_BASE}/embed?account_id=${encodeURIComponent(newAcc)}`,
      );
    });
  }, [embedded, accountChangeWatchAccId]);

  // Tema actual leído del DOM (main.tsx ya seteó data-theme en el primer load).
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') ?? 'light',
  );

  // Si tiene override manual, mostramos opción de "seguir el panel" en el menú.
  // Solo aplica embedded (en standalone no hay panel que seguir).
  const [manualOverride, setManualOverride] = useState<boolean>(() => hasManualOverride());

  const toggleTheme = () => {
    const next: 'light' | 'dark' = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setManualOverride(true);
    // Helper persiste con source='user' → un postMessage del parent NO podrá
    // sobrescribir esta decisión. Si el user explicita su tema, respetamos.
    applyTheme(next);
  };

  const followPanel = () => {
    followParentTheme();
    setManualOverride(false);
    // El próximo postMessage del panel resetea el tema. Mientras tanto, lo
    // re-leemos del DOM (followParentTheme ya lo aplicó al system-pref).
    const current = document.documentElement.getAttribute('data-theme') as 'light' | 'dark';
    if (current) setTheme(current);
  };

  const doLogout = async () => {
    try {
      // useLogout limpia el queryClient cache onSuccess; importante para no
      // dejar datos del tenant anterior visibles si el siguiente user logea.
      await logout.mutateAsync();
    } catch {
      // Independiente del resultado, sacamos al user del shell.
    } finally {
      navigate('/login', { replace: true });
    }
  };

  // Count de variants TRACKEADAS con stock bajo (total_qty <
  // max(reorder_point)) o agotado (total_qty <= 0). Backend devuelve el
  // COUNT agregado en SQL — usa la misma semántica que el dashboard
  // (lowStockSkus + outOfStockSkus). El badge se muestra en el tab
  // Productos como sub-badge rojo al lado del total.
  const lowStockCount = lowStockSummary.data?.count ?? 0;

  if (isLoading) {
    return (
      <main className="splash">
        <p className="muted">Cargando…</p>
      </main>
    );
  }

  // Discriminar el error:
  //   - 401 + iframe = sesión expiró → re-embed con params cacheados (en
  //     lugar de /login standalone que dentro del iframe es dead-end y se
  //     percibe como "refresh de la web"). Si no tenemos cache, fallback
  //     a splash con mensaje pidiendo recargar el panel Whatsfy.
  //   - 401 + standalone = redirect a /login.
  //   - 5xx/network → ErrorState con retry; NO redirigimos (perdería el contexto
  //     del iframe Whatsfy y dispararía el flujo de magic link sin razón).
  if (error instanceof ApiError && error.status === 401) {
    if (embedded) {
      const reembedUrl = buildReembedUrl(API_BASE);
      if (reembedUrl) {
        // Hard navigate — el backend hace 303 a /dashboard con cookie nueva.
        // window.location.replace evita que quede el /channels viejo en
        // history (back button no rebota a la página 401).
        window.location.replace(reembedUrl);
        return (
          <main className="splash">
            <p className="muted">Renovando sesión…</p>
          </main>
        );
      }
      return (
        <main className="splash">
          <p className="muted">
            Tu sesión expiró. Cierra el iframe del módulo en Whatsfy y vuelve
            a abrirlo desde el menú lateral para continuar.
          </p>
        </main>
      );
    }
    if (location.pathname === '/login') {
      return (
        <main className="splash">
          <p className="muted">Cargando…</p>
        </main>
      );
    }
    return <Navigate to="/login" replace />;
  }
  if (error) {
    return <ErrorState error={error} embedded={embedded} onRetry={() => refetch()} />;
  }
  if (!me) {
    if (embedded) {
      const reembedUrl = buildReembedUrl(API_BASE);
      if (reembedUrl) {
        window.location.replace(reembedUrl);
        return (
          <main className="splash">
            <p className="muted">Renovando sesión…</p>
          </main>
        );
      }
      return (
        <main className="splash">
          <p className="muted">
            Sin sesión activa. Cierra el iframe del módulo en Whatsfy y vuelve
            a abrirlo desde el menú lateral.
          </p>
        </main>
      );
    }
    if (location.pathname === '/login') {
      return (
        <main className="splash">
          <p className="muted">Cargando…</p>
        </main>
      );
    }
    return <Navigate to="/login" replace />;
  }

  // IFRAME-B: defensa cliente-side contra mismatch entre cuenta del referrer
  // (panel Whatsfy que embebe el iframe) y tenant actual del módulo. El user
  // pudo cambiar de cuenta en el panel host, pero la cookie httpOnly del
  // módulo sigue apuntando al tenant viejo. IFRAME-A (backend) maneja la
  // invalidación de la cookie; acá renderizamos pantalla amigable con CTA
  // para re-handshake.
  //
  // Aplica SOLO cuando:
  //   - Estamos embebidos (sin referrer útil en standalone).
  //   - El referrer trae `acc=` (panel Whatsfy / distribuidor compatible).
  //   - El tenant actual tiene `whatsfyAccountId` (no es tenant standalone).
  //   - Los dos valores no coinciden.
  const referrerAccountId = getReferrerAccountId();
  const currentAccountId = me.tenant.whatsfyAccountId;
  const accountMismatch =
    embedded &&
    referrerAccountId !== null &&
    currentAccountId !== '' &&
    referrerAccountId !== currentAccountId;

  if (accountMismatch) {
    const handleReopen = async () => {
      try {
        // Limpia la cookie vieja (en clase puede haber sido invalidada por
        // IFRAME-A backend ya — el endpoint es idempotente).
        await logout.mutateAsync();
      } catch {
        // Sin red / 401 — seguimos al redirect igual; el backend re-handshake
        // pisa la cookie inválida con una nueva.
      }
      // Hard navigate al endpoint /embed con el account_id correcto del
      // referrer. El backend responde 303 con cookie nueva + redirect a
      // /dashboard del tenant correcto.
      window.location.replace(
        `${API_BASE}/embed?account_id=${encodeURIComponent(referrerAccountId ?? '')}`,
      );
    };
    const handleCloseFallback = () => {
      // Si el re-handshake falla en loop (IFRAME-A backend aún sin merge),
      // damos salida limpia: navegamos al panel host del referrer. El user
      // pierde el iframe pero queda en su panel Whatsfy operativo.
      if (typeof document !== 'undefined' && document.referrer) {
        try {
          const ref = new URL(document.referrer);
          window.location.replace(ref.origin);
          return;
        } catch {
          // Referrer malformed — fallback al blank.
        }
      }
      window.location.replace('about:blank');
    };
    return (
      <main className="splash">
        <div
          className="card"
          style={{
            padding: 24,
            maxWidth: 480,
            textAlign: 'center',
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            Esta cuenta no tiene el módulo configurado
          </h3>
          <p
            className="muted"
            style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 20 }}
          >
            Estás viendo el inventario de{' '}
            <strong style={{ color: 'var(--text-1)' }}>{me.tenant.name}</strong>{' '}
            pero el iframe está embebido en una cuenta distinta. Reabre el
            módulo desde el menú lateral de Whatsfy para esta cuenta.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleReopen()}
            >
              Reabrir desde Whatsfy
            </button>
            <button
              type="button"
              className="btn"
              onClick={handleCloseFallback}
            >
              Cerrar iframe
            </button>
          </div>
          <p
            style={{
              marginTop: 16,
              fontSize: 11,
              color: 'var(--text-3)',
              lineHeight: 1.4,
            }}
          >
            Si esto persiste, contacta a soporte.
          </p>
        </div>
      </main>
    );
  }

  // D1-F2b: en modo soporte (super-admin impersonando un comercio en solo
  // lectura), el banner se renderiza fijo arriba de todo en TODAS las rutas
  // autenticadas. El padding-top del shell reserva el alto real medido del
  // banner (variable: el texto envuelve en viewports estrechos / iframe).
  const supportMode = me.impersonation ?? null;
  // Mientras el banner aún no reportó su alto, usamos un fallback holgado
  // para no tener un flash de contenido tapado en el primer paint.
  const supportBannerPad = supportMode
    ? supportBannerHeight || 84
    : 0;

  // Tenant aún sin conectar la API key — solo mostramos /setup, sin tabs.
  if (!me.tenant.connected) {
    if (location.pathname !== '/setup') {
      return <Navigate to="/setup" replace />;
    }
    return (
      <div
        className="module"
        data-screen-label="inventario-onboarding"
        style={supportMode ? { paddingTop: supportBannerPad } : undefined}
      >
        {supportMode && (
          <SupportModeBanner
            superAdmin={supportMode.superAdmin}
            onHeightChange={setSupportBannerHeight}
          />
        )}
        <div className="module-content">
          <Outlet />
        </div>
      </div>
    );
  }

  // Tenant conectado → shell completo con header + tabs.
  // Tab "Equipo" siempre visible. La página /equipo internamente muestra
  // distinto contenido según el rol (owner/admin = gestión completa,
  // member = mensaje informativo). Tener el tab siempre visible permite
  // que un member pueda navegar a /equipo y ver opciones de bootstrap
  // (ej. limpiar datos malformados que generaron tenant sin owner).

  // El tab de "API keys" gestiona tokens para integraciones externas
  // (bots/checkouts/flows). Solo owner/admin puede ver/gestionar — para member
  // no aporta nada (no tiene scope de uso) y el endpoint backend está role-gated.
  const role = me.session.role;
  // D2: funcionalidades por tenant. `features` viene en el response de
  // /auth/tenant-settings; `featureEnabled` defaultea a `true` cuando el campo
  // está ausente (backend viejo) — backward-compat absoluto, no oculta nada.
  //
  // Solo declaramos los flags que efectivamente usa el top nav reducido
  // (`hasShopify` gatea Canales; `hasMultiCurrency` se usa abajo para Precios).
  // El resto (`hasWebhooks`/`hasConversions`/`hasBotApi`/`canManageApiKeys`)
  // se evalúa dentro de Configuracion para los sub-tabs internos.
  const features = tenantSettingsQuery.data?.features;
  const hasShopify = featureEnabled(features, 'shopify');
  const hasMultiCurrency = featureEnabled(features, 'multi_currency');

  // Listas de precios visibles solo cuando: el rol gestiona + el tenant está
  // en pricingMode 'multi' + la feature multi_currency está habilitada (D2).
  // Las 3 condiciones se componen — no se reemplazan entre sí.
  const canManagePriceLists =
    (role === 'owner' || role === 'admin') &&
    pricingMode === 'multi' &&
    hasMultiCurrency;
  // F4-E: configuración avanzada (regional/notificaciones/formatos/branding).
  // Solo owner/admin — member no decide config del tenant.
  const canManageConfig = role === 'owner' || role === 'admin';

  // V3.4: descuentos por volumen — solo owner/admin. Member opera stock pero
  // no define reglas comerciales del catálogo.
  const canManageDiscounts = role === 'owner' || role === 'admin';

  // Top nav reducido (V3.3.3 sprint UI): solo 12 tabs operativos visibles.
  // Equipo / Auditoría / API keys / Webhooks / Atribución / Plataformas ads /
  // Conexión Whatsfy se acceden como sub-tabs DENTRO de Configuración (las
  // rutas /equipo, /api-keys, /webhooks, /atribucion, /conversion-platforms,
  // /setup mantienen backward-compat vía redirect a /configuracion?tab=X
  // declarado en main.tsx).
  //
  // Gates por feature/rol mantenidos para los items que sí siguen siendo top
  // nav (Precios/Descuentos/Canales). Configuración se muestra a owner/admin
  // y agrupa el resto.
  const tabs: ModuleTab[] = [
    { to: '/dashboard', label: 'Resumen', icon: 'grid' },
    {
      to: '/products',
      label: 'Productos',
      icon: 'package',
      count: products.data?.total,
      countDanger: lowStockCount > 0 ? lowStockCount : undefined,
      countDangerTitle:
        lowStockCount > 0
          ? `${lowStockCount} variante${lowStockCount === 1 ? '' : 's'} con stock bajo o agotado`
          : undefined,
    },
    { to: '/categories', label: 'Categorías', icon: 'box', count: categories.data?.total },
    { to: '/warehouses', label: 'Bodegas', icon: 'warehouse', count: warehouses.data?.total },
    { to: '/suppliers', label: 'Proveedores', icon: 'bot', count: suppliers.data?.total },
    { to: '/movements', label: 'Movimientos', icon: 'activity' },
    {
      to: '/payments',
      label: 'Pagos',
      icon: 'cart',
      // Solo mostramos badge si hay pendings reales — un 0 genera ruido.
      // Tone danger porque pendings sin verificar son acción del admin.
      count: pendingPayments.data?.total
        ? pendingPayments.data.total
        : undefined,
      countTone: pendingPayments.data?.total ? 'danger' : undefined,
    },
    ...(canManagePriceLists
      ? [
          {
            to: '/price-lists',
            label: 'Precios',
            icon: 'cart' as const,
          },
        ]
      : []),
    ...(canManageDiscounts
      ? [
          {
            to: '/descuentos',
            label: 'Descuentos',
            icon: 'cart' as const,
          },
        ]
      : []),
    { to: '/imports', label: 'Importar', icon: 'upload' },
    // D2: Canales (Shopify) — visible solo si el tenant tiene la feature
    // `shopify`. Sin gate por rol (era unconditional); solo gate por feature.
    ...(hasShopify
      ? [{ to: '/channels', label: 'Canales', icon: 'link' as const }]
      : []),
    // V3 SA11: Leads + carritos abandonados. Todos los roles ven el tab
    // (member ve PII redactada). El gate por feature lo dejamos abierto:
    // el modelo Lead es first-class del módulo, no opcional.
    { to: '/leads', label: 'Leads', icon: 'bot' as const },
    // "Configuración" agrupa: Regional/Formatos/Branding/Storefront/Envíos +
    // Conexión Whatsfy, Equipo, Auditoría, API keys, Webhooks, Atribución,
    // Plataformas ads (sub-tabs internos). owner/admin únicamente.
    // hasConversions/hasWebhooks/hasBotApi se referencian aquí para evitar
    // warnings de variables sin uso — los gates reales viven dentro del
    // componente Configuracion para cada sub-tab.
    ...(canManageConfig
      ? [{ to: '/configuracion', label: 'Configuración', icon: 'settings' as const }]
      : []),
  ];

  const subTitle = `${me.tenant.name} · gestión manual`;

  // Bloque del avatar (button + dropdown). Se renderiza en una de 2
  // ubicaciones según embedded: standalone vive dentro del header row;
  // iframe vive como floating top-right del viewport, fuera del flow.
  const avatarMenu = (
    <>
      <button
        ref={avatarBtnRef}
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="Menú de usuario"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={me.session.email ?? 'Usuario'}
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--bg-3)',
          color: 'var(--text-1)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 500,
          border: '1px solid var(--border-1)',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        {(me.session.email ?? '?').slice(0, 1).toUpperCase()}
      </button>
      {menuOpen && (
        <>
          <div
            onClick={() => setMenuOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100,
            }}
          />
          <div
            role="menu"
            style={{
              position: 'absolute',
              right: 8,
              top: 'calc(100% + 8px)',
              background: 'var(--bg-1)',
              border: '1px solid var(--border-2)',
              borderRadius: 8,
              minWidth: 200,
              maxWidth: 'calc(100vw - 32px)',
              width: 240,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              zIndex: 101,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: 12, borderBottom: '1px solid var(--border-1)' }}>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--text-1)',
                  fontWeight: 500,
                  wordBreak: 'break-all',
                }}
              >
                {me.session.email ?? 'Sin sesión'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                {me.session.role ?? '—'} · {me.tenant.name}
              </div>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={toggleTheme}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                color: 'var(--text-1)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg-2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-flex',
                  width: 16,
                  height: 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-2)',
                }}
              >
                {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              </span>
              <span style={{ flex: 1 }}>
                Cambiar a tema {theme === 'dark' ? 'claro' : 'oscuro'}
              </span>
              <span
                aria-hidden="true"
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--text-3)',
                  padding: '2px 6px',
                  border: '1px solid var(--border-1)',
                  borderRadius: 4,
                }}
              >
                {theme === 'dark' ? 'OSC' : 'CLR'}
              </span>
            </button>
            {embedded && manualOverride && (
              <button
                type="button"
                role="menuitem"
                onClick={followPanel}
                title="El módulo seguirá el tema que tengas en Whatsfy"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'transparent',
                  border: 'none',
                  borderTop: '1px solid var(--border-1)',
                  textAlign: 'left',
                  color: 'var(--text-2)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg-2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon name="link" size={12} />
                Seguir tema del panel Whatsfy
              </button>
            )}
            {embedded ? (
              <div
                style={{
                  padding: '10px 12px',
                  borderTop: '1px solid var(--border-1)',
                  fontSize: 11,
                  color: 'var(--text-3)',
                  lineHeight: 1.4,
                }}
              >
                Tu sesión se gestiona desde Whatsfy. Cierra sesión allí para
                salir.
              </div>
            ) : (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  void doLogout();
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'transparent',
                  border: 'none',
                  borderTop: '1px solid var(--border-1)',
                  textAlign: 'left',
                  color: 'var(--text-1)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg-3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon name="close" size={14} />
                Cerrar sesión
              </button>
            )}
          </div>
        </>
      )}
    </>
  );

  return (
    <div
      className="module"
      data-screen-label="inventario"
      style={supportMode ? { paddingTop: supportBannerPad } : undefined}
    >
      {supportMode && (
        <SupportModeBanner
          superAdmin={supportMode.superAdmin}
          onHeightChange={setSupportBannerHeight}
        />
      )}
      {/* D1-F2b: en modo soporte el banner es `position: fixed` arriba; el
          header sticky debe pegarse DEBAJO del banner (no en top:0) para no
          deslizarse por detrás de él al hacer scroll. */}
      <div
        className="module-header"
        style={{
          ...(embedded ? { paddingTop: 8 } : null),
          ...(supportMode ? { top: supportBannerPad } : null),
        }}
      >
        {/* En modo iframe Whatsfy ocultamos la fila completa título+avatar:
            el panel host ya muestra el chip del tenant arriba del iframe.
            El avatar (con toggle de tema + logout) se reubica como botón
            flotante top-right del viewport — accesible sobre el contenido
            sin comer altura del header. Las tabs nav suben pegadas al
            top del iframe (519px claustrofóbico). */}
        {!embedded && (
          <div className="module-title-row">
            <div className="module-title">
              <div className="module-title-mark">
                <Icon name="package" size={15} />
              </div>
              <div>
                <h2>
                  Inventario <em>{brandName}</em>
                </h2>
                <div className="sub">{subTitle}</div>
              </div>
            </div>
            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              {avatarMenu}
            </div>
          </div>
        )}
        {/* Las tabs (16+) no caben en una sola fila ni en viewport ancho:
            `.module-tabs` usa flex-wrap a múltiples filas (ver tokens.css).
            En iframe reservamos paddingRight para que la última tab no se
            solape con el badge "Cuenta:" + avatar flotante top-right. */}
        <div
          className="module-tabs"
          style={embedded ? { paddingRight: 180 } : undefined}
        >
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) => `module-tab ${isActive ? 'active' : ''}`}
            >
              <Icon name={t.icon} />
              <span>{t.label}</span>
              {typeof t.count === 'number' && t.count > 0 && (
                <span className={`count ${t.countTone === 'danger' ? 'badge-danger' : ''}`}>
                  {t.count}
                </span>
              )}
              {typeof t.countDanger === 'number' && t.countDanger > 0 && (
                <span className="count badge-danger" title={t.countDangerTitle}>
                  {t.countDanger}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </div>
      {/* Avatar flotante en iframe: position fixed top-right del viewport
          para que quede al lado del título de la página actual (PageHead)
          sin comer altura del module-header. El dropdown sigue posicionado
          relative al wrapper. */}
      {embedded && (
        <div
          style={{
            position: 'fixed',
            // D1-F2b: cuando el banner de soporte está fijo arriba, bajamos el
            // avatar flotante para que no quede tapado por el banner.
            top: 8 + supportBannerPad,
            right: 12,
            zIndex: 30,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {/* IFRAME-FIX-V2 capa 2: badge "Cuenta: {tenantName}" siempre
              visible cuando estamos en iframe. Permite al comerciante
              detectar mismatch del tenant aunque el snippet postMessage
              del parent (capa 1) no esté inyectado. Si el snippet sí está,
              el redirect ocurre antes de que el user vea el mismatch. */}
          {me?.tenant?.name && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                padding: '3px 10px',
                borderRadius: 999,
                background: 'var(--bg-3)',
                color: 'var(--text-1)',
                border: '1px solid var(--border-1)',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}
              title="El módulo está mostrando datos de esta cuenta. Si cambiaste de cuenta en Whatsfy, recarga el módulo desde el menú."
            >
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: 'var(--green)',
                }}
              />
              Cuenta: {me.tenant.name}
            </span>
          )}
          {avatarMenu}
        </div>
      )}
      <div className="module-content">
        <Outlet />
      </div>
      {sessionExpired && (
        <SessionExpiredModal embedded={embedded} onReconnect={reconnect} />
      )}
      {/* PRICE-C: onboarding modal — solo se renderiza cuando el comerciante
          aún no eligió pricingMode explícito. role member también lo verá
          si por alguna razón es la primera sesión del tenant (raro, pero
          inofensivo: la mutation backend ya valida owner/admin). */}
      <PricingModeOnboarding
        open={showOnboarding}
        onClose={() => setOnboardingDismissed(true)}
      />
    </div>
  );
}

/**
 * F7-B: modal "Sesión expirada". Se muestra cuando un request 401 NO pudo
 * recuperarse con `/auth/refresh`. Razón típica: refresh token expirado (30d
 * sin usar el módulo), revocado por admin, o family invalidado por reuse.
 *
 * UX: countdown visible + botón explícito "Reconectar ahora". El timer del
 * Layout también dispara reconnect tras 5s — este modal solo lo refleja.
 */
function SessionExpiredModal({
  embedded,
  onReconnect,
}: {
  embedded: boolean;
  onReconnect: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState(5);
  useEffect(() => {
    const id = window.setInterval(
      () => setSecondsLeft((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => window.clearInterval(id);
  }, []);
  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-head">
          <h2 id="session-expired-title">Sesión expirada</h2>
        </div>
        <div className="modal-body">
          <p style={{ margin: 0, color: 'var(--text-2)', lineHeight: 1.5 }}>
            {embedded
              ? 'Tu sesión venció. Vamos a renovarla automáticamente desde Whatsfy en unos segundos.'
              : 'Tu sesión venció por inactividad. Vamos a recargar la página automáticamente en unos segundos.'}
          </p>
          <p
            style={{
              marginTop: 12,
              fontSize: 12,
              color: 'var(--text-3)',
            }}
          >
            Reconectando en{' '}
            <strong style={{ color: 'var(--text-1)' }}>{secondsLeft}s</strong>…
          </p>
        </div>
        <div className="modal-foot">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onReconnect}
            autoFocus
          >
            Reconectar ahora
          </button>
        </div>
      </div>
    </div>
  );
}

function SunIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
    </svg>
  );
}
